import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  Filters,
  Layout,
  Link,
  Page,
  Pagination,
  Select,
  Spinner,
  Toast,
} from '@shopify/polaris'
import React, { useCallback, useEffect, useState } from 'react'

import FormattedDateTime from '../components/FormattedDateTime'
import { useAdminApi } from '../hooks/useAdminApi'
import { useAdminAuth } from '../providers/AdminAuthProvider'

interface Shop {
  id: number
  myshopify_domain: string
  domain?: string
  email: string
  name: string
  shop_owner?: string
  is_installed: boolean
  installed_at?: string
  country?: string
  currency: string
  shop_created_at: string
  created_at: string
  plan: {
    public_display_name: string
    shopify_plus: boolean
    partner_development: boolean
  }
}

interface ShopsResponse {
  shops: Shop[]
  total: number
  page: number
  limit: number
  total_pages: number
}

export default function AdminShopsPage() {
  const { listShops, generateShopToken } = useAdminApi()
  const { hasPermission } = useAdminAuth()
  const [shops, setShops] = useState<Shop[]>([])
  const [loading, setLoading] = useState(true)
  const [searchText, setSearchText] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [generatingTokenFor, setGeneratingTokenFor] = useState<number | null>(
    null
  )
  const [toastMessage, setToastMessage] = useState('')
  const [toastError, setToastError] = useState(false)
  const [filters, setFilters] = useState({
    country: '',
    currency: '',
    is_installed: '',
    shopify_plus: '',
    public_display_name: '',
  })

  const fetchShops = useCallback(async () => {
    setLoading(true)
    const params: Record<string, any> = {
      page: currentPage,
      limit: 20,
    }

    if (searchText) params.search = searchText
    if (filters.country) params.country = filters.country
    if (filters.currency) params.currency = filters.currency
    if (filters.is_installed) params.is_installed = filters.is_installed
    if (filters.shopify_plus) params.shopify_plus = filters.shopify_plus
    if (filters.public_display_name)
      params.public_display_name = filters.public_display_name

    const response = await listShops(params)
    if (response.success && response.data) {
      const data = response.data as ShopsResponse
      setShops(data.shops)
      setTotalPages(data.total_pages)
    }
    setLoading(false)
  }, [listShops, currentPage, searchText, filters])

  useEffect(() => {
    fetchShops()
  }, [fetchShops])

  const handleSearchChange = useCallback((value: string) => {
    setSearchText(value)
    setCurrentPage(1)
  }, [])

  const handleFilterChange = useCallback((key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setCurrentPage(1)
  }, [])

  const clearFilters = useCallback(() => {
    setFilters({
      country: '',
      currency: '',
      is_installed: '',
      shopify_plus: '',
      public_display_name: '',
    })
    setSearchText('')
    setCurrentPage(1)
  }, [])

  const showToast = (message: string, isError = false) => {
    setToastMessage(message)
    setToastError(isError)
  }

  const handleShopLogin = async (shop: Shop) => {
    if (!hasPermission('login_shops')) {
      showToast('You do not have permission to login to shops', true)
      return
    }

    setGeneratingTokenFor(shop.id)
    try {
      const response = await generateShopToken(shop.myshopify_domain)
      if (response.success && response.data?.token) {
        const loginUrl = `${import.meta.env.VITE_FE_APP}?token=${response.data.token}`
        window.open(loginUrl, '_blank')
      } else {
        showToast(response.error || 'Failed to generate shop token', true)
      }
    } catch (error) {
      console.error('Failed to generate shop token:', error)
      showToast('Failed to generate shop token', true)
    } finally {
      setGeneratingTokenFor(null)
    }
  }

  const filterOptions = [
    {
      key: 'country',
      label: 'Country',
      filter: (
        <Select
          label="Country"
          value={filters.country}
          onChange={value => handleFilterChange('country', value)}
          options={[
            { label: 'All countries', value: '' },
            { label: 'United States', value: 'US' },
            { label: 'Canada', value: 'CA' },
            { label: 'United Kingdom', value: 'GB' },
            { label: 'Australia', value: 'AU' },
          ]}
        />
      ),
    },
    {
      key: 'is_installed',
      label: 'Status',
      filter: (
        <Select
          label="Installation Status"
          value={filters.is_installed}
          onChange={value => handleFilterChange('is_installed', value)}
          options={[
            { label: 'All shops', value: '' },
            { label: 'Installed', value: 'true' },
            { label: 'Uninstalled', value: 'false' },
          ]}
        />
      ),
    },
    {
      key: 'shopify_plus',
      label: 'Plan Type',
      filter: (
        <Select
          label="Shopify Plus"
          value={filters.shopify_plus}
          onChange={value => handleFilterChange('shopify_plus', value)}
          options={[
            { label: 'All plans', value: '' },
            { label: 'Shopify Plus', value: 'true' },
            { label: 'Regular', value: 'false' },
          ]}
        />
      ),
    },
    {
      key: 'public_display_name',
      label: 'Plan Name',
      filter: (
        <Select
          label="Plan Display Name"
          value={filters.public_display_name}
          onChange={value => handleFilterChange('public_display_name', value)}
          options={[
            { label: 'All plans', value: '' },
            { label: 'Basic Shopify', value: 'Basic Shopify' },
            { label: 'Shopify', value: 'Shopify' },
            { label: 'Advanced Shopify', value: 'Advanced Shopify' },
            { label: 'Plus', value: 'Plus' },
            { label: 'Development', value: 'Development' },
          ]}
        />
      ),
    },
  ]

  const canLoginShops = hasPermission('login_shops')
  const partnerStoresUrl =
    import.meta.env.VITE_SHOPIFY_PARTNER_STORES_URL ||
    'https://partners.shopify.com/4486054/stores/'

  // Build column content types dynamically
  const columnContentTypes = [
    'text' as const, // ID
    ...(canLoginShops ? ['text' as const] : []), // Login (conditional)
    'text' as const, // MyShopify Domain
    'text' as const, // Shop Name
    'text' as const, // Owner Name
    'text' as const, // Email
    'text' as const, // Country
    'text' as const, // Status
    'text' as const, // Installed Date
    'text' as const, // Plan
  ]

  // Build headings dynamically
  const headings = [
    'ID',
    ...(canLoginShops ? ['Login'] : []), // Login (conditional)
    'Domain', // MyShopify Domain
    'Shop Name',
    'Owner',
    'Email',
    'Country',
    'Status',
    'Installed',
    'Plan',
  ]

  const rows = shops.map(shop => {
    const row: (string | React.ReactElement)[] = [
      // ID column - clickable to open partner stores
      <Link
        url={`${partnerStoresUrl}${shop.id}`}
        target="_blank"
        removeUnderline
      >
        {shop.id.toString()}
      </Link>,
    ]

    // Add Login column if user has permission
    if (canLoginShops) {
      row.push(
        shop.is_installed ? (
          <Button
            size="micro"
            variant="primary"
            loading={generatingTokenFor === shop.id}
            onClick={() => handleShopLogin(shop)}
            disabled={generatingTokenFor !== null}
          >
            Login
          </Button>
        ) : (
          <span style={{ color: '#6b7280' }}>Not installed</span>
        )
      )
    }

    // Add remaining columns
    row.push(
      // MyShopify Domain - clickable to open shop
      <Link
        url={`https://${shop.myshopify_domain}`}
        target="_blank"
        removeUnderline
      >
        {shop.myshopify_domain}
      </Link>,
      // Shop Name
      shop.name,
      // Owner Name
      shop.shop_owner || '-',
      // Email
      shop.email,
      // Country
      shop.country || '-',
      // Installation Status
      <Badge tone={shop.is_installed ? 'success' : 'critical'}>
        {shop.is_installed ? 'Installed' : 'Uninstalled'}
      </Badge>,
      // Installed Date
      shop.installed_at ? <FormattedDateTime date={shop.installed_at} /> : '-',
      // Plan
      <div style={{ display: 'flex', flexDirection: 'row', gap: '4px' }}>
        <div>{shop.plan.public_display_name}</div>
        <div style={{ fontSize: '0.8em', color: '#6b7280', marginTop: '2px' }}>
          {shop.plan.shopify_plus && (
            <Badge tone="info" size="small">
              Plus
            </Badge>
          )}
          {shop.plan.partner_development && (
            <Badge tone="warning" size="small">
              Dev
            </Badge>
          )}
        </div>
      </div>
    )

    return row
  })

  return (
    <Page title="Shop Management">
      <Layout>
        <Layout.Section>
          <Card>
            <div style={{ padding: '16px' }}>
              <Filters
                queryValue={searchText}
                queryPlaceholder="Search shops..."
                onQueryChange={handleSearchChange}
                onQueryClear={() => handleSearchChange('')}
                filters={filterOptions}
                onClearAll={clearFilters}
              />
            </div>

            {loading ? (
              <div style={{ padding: '32px', textAlign: 'center' }}>
                <Spinner size="large" />
              </div>
            ) : shops.length === 0 ? (
              <EmptyState heading="No shops found" image="">
                <p>Try adjusting your search or filter criteria.</p>
              </EmptyState>
            ) : (
              <>
                <DataTable
                  columnContentTypes={columnContentTypes}
                  headings={headings}
                  rows={rows}
                />

                {totalPages > 1 && (
                  <div
                    style={{
                      padding: '16px',
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <Pagination
                      hasPrevious={currentPage > 1}
                      onPrevious={() => setCurrentPage(currentPage - 1)}
                      hasNext={currentPage < totalPages}
                      onNext={() => setCurrentPage(currentPage + 1)}
                    />
                  </div>
                )}
              </>
            )}
          </Card>
        </Layout.Section>
      </Layout>
      {toastMessage && (
        <Toast
          content={toastMessage}
          error={toastError}
          onDismiss={() => setToastMessage('')}
        />
      )}
    </Page>
  )
}
